import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkIsActive, KTIcon, WithChildren } from '../../../../helpers';
import { useLayout } from '../../../core';
import { SidebarMenuItem } from './SidebarMenuItem';
import { api, sendData } from '../../../../helpers/utils/api';
import { Link } from 'react-router-dom';
import { cmsConfirm, cmsAlert } from '../../../../../app/components/CmsModal';

type Props = {
  to: string;
  title: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
};

const SidebarMenuTemplateItem: React.FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isActive = checkIsActive(pathname, to);
  const { config } = useLayout();
  const { app } = config;

  const [pages, setPages] = useState<{ id: number; name: string, slug?: string }[]>([]);
  const [hiddenPages, setHiddenPages] = useState<number[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newPageName, setNewPageName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = React.useRef(false);

  const STANDARD_PAGES = [
    'home', 'career', 'contact', 'loan', 'loans', 'privacy', 'faq', 'business', 
    'template', 'about', 'blog', 'resource', 'blogdetail', 'navbar', 'footer',
    'digitization', 'mentorship', 'financialliteracy', 'grants', 'getinvolved', 
    'corporate', 'successstories', 'walktogethertour', 'programs'
  ];

  useEffect(() => {
    const loadPages = async () => {
      try {
        const response = await api.get('page');
        if (response.data && Array.isArray(response.data.allPages)) {
          const backendPages = response.data.allPages;
          
          // Custom pages are any pages that aren't hardcoded in the frontend
          const customPagesRaw = backendPages.filter((p: any) => !STANDARD_PAGES.includes(String(p.name).toLowerCase()));
          
          // Fetch titles for custom pages
          const titlesRes = await api.get('data', { params: { sectionName: 'PageMetadata', key: 'title' } });
          const titlesData = titlesRes.data?.data || [];
          
          // Fetch hidden states
          const hiddenRes = await api.get('data', { params: { sectionName: 'PageMetadata', key: 'isHidden' } });
          const hiddenData = hiddenRes.data?.data || [];
          
          const parsedHiddenIds: number[] = [];
          
          const mappedPages = customPagesRaw.map((p: any) => {
            const titleRecord = titlesData.find((d: any) => d.pageId === p.id);
            const hiddenRecord = hiddenData.find((d: any) => d.pageId === p.id);
            
            if (hiddenRecord && hiddenRecord.valueEnglish === 'true') {
              parsedHiddenIds.push(p.id);
            }
            
            return {
              id: p.id,
              slug: p.name,
              name: titleRecord?.valueEnglish || p.name
            };
          });
          
          setHiddenPages(parsedHiddenIds);
          setPages(mappedPages);
        }
      } catch (e) {
        console.error('Failed to load pages', e);
      }
    };
    loadPages();
    window.addEventListener('template_pages_updated', loadPages);
    return () => window.removeEventListener('template_pages_updated', loadPages);
  }, []);

  const handleAddClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
  };

  const createPage = async (name: string) => {
    if (!name.trim() || isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    
    try {
      // Create a slug from the name
      let baseSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
      if (!baseSlug) baseSlug = 'custom-page';

      // Fetch all pages to check for slug collisions
      const response = await api.get('page');
      const allPages = response.data?.allPages || [];
      
      let finalSlug = baseSlug;
      let counter = 1;
      while (allPages.some((p: any) => p.name === finalSlug)) {
        finalSlug = `${baseSlug}-${counter}`;
        counter++;
      }

      // Create in backend (using slug as the name in backend)
      const createRes = await api.post('page', { name: finalSlug });
      const newPage = createRes.data;

      if (newPage && newPage.id) {
        // Save the real page ID and human name to the CustomPageBlocks data so TemplatePageEditor knows the real name
        await api.post('data', {
          pageId: newPage.id,
          sectionName: 'PageMetadata',
          key: 'title',
          valueEnglish: name.trim()
        });

        setPages(prev => [...prev, { id: newPage.id, slug: finalSlug, name: name.trim() }]);
        
        // Navigate to the newly created page
        navigate(`${to}/${newPage.id}`);
      }
    } catch (e) {
      console.error('Failed to create page', e);
      cmsAlert({ title: 'Error', description: 'Failed to create page. Please try again.', variant: 'danger', icon: 'bi bi-exclamation-triangle' });
    } finally {
      isSavingRef.current = false;
      setIsAdding(false);
      setNewPageName('');
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      createPage(newPageName);
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setNewPageName('');
    }
  };

  const handleDelete = async (e: React.MouseEvent, pageId: number, pageName: string) => {
    e.preventDefault();
    e.stopPropagation();
    const confirmed = await cmsConfirm({
      title: 'Delete Page',
      description: 'Are you sure you want to delete this page permanently? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
      icon: 'bi bi-trash',
    });
    if (confirmed) {
      try {
        await api.delete(`page/${pageId}`);
        setPages(prev => prev.filter(p => p.id !== pageId));
        
        // If the user is currently on the deleted page, navigate away
        if (pathname === `${to}/${pageId}`) {
          navigate('/pages/home/intro');
        }
      } catch (err) {
        console.error('Failed to delete page', err);
        cmsAlert({ title: 'Error', description: 'Failed to delete page. Please try again.', variant: 'danger', icon: 'bi bi-exclamation-triangle' });
      }
    }
  };

  const handleHideToggle = async (e: React.MouseEvent, pageId: number, currentHiddenState: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    
    const action = currentHiddenState ? 'unhide' : 'hide';
    const confirmMessage = currentHiddenState 
      ? 'This page will be visible on the public website again.'
      : 'This page will be hidden from the public website.';
    
    const confirmed = await cmsConfirm({
      title: currentHiddenState ? 'Show Page' : 'Hide Page',
      description: confirmMessage,
      confirmText: currentHiddenState ? 'Show Page' : 'Hide Page',
      variant: 'warning',
      icon: currentHiddenState ? 'bi bi-eye' : 'bi bi-eye-slash',
    });
    if (confirmed) {
      try {
        await sendData({
          pageId: pageId,
          sectionName: 'PageMetadata',
          key: 'isHidden',
          valueEnglish: (!currentHiddenState).toString()
        });
        
        setHiddenPages(prev => {
          if (!currentHiddenState) {
            return [...prev, pageId];
          }
          return prev.filter(id => id !== pageId);
        });
      } catch (err) {
        console.error('Failed to toggle visibility', err);
        cmsAlert({ title: 'Error', description: `Failed to ${action} page.`, variant: 'danger', icon: 'bi bi-exclamation-triangle' });
      }
    }
  };

  return (
    <div
      className={clsx('menu-item', { 'here': isActive }, 'menu-accordion')}
      data-kt-menu-trigger='click'
    >
      <span className='menu-link'>
        {hasBullet && (
          <span className='menu-bullet'>
            <span className='bullet bullet-dot'></span>
          </span>
        )}
        {icon && app?.sidebar?.default?.menu?.iconType === 'svg' && (
          <span className='menu-icon'>
            <KTIcon iconName={icon} className='fs-2' />
          </span>
        )}
        {fontIcon && app?.sidebar?.default?.menu?.iconType === 'font' && (
          <i className={clsx('bi fs-3', fontIcon)}></i>
        )}
        <span className='menu-title w-100'>
          {title}
        </span>
        <span className='menu-arrow'></span>
      </span>
      <div className={clsx('menu-sub menu-sub-accordion', { 'menu-active-bg': isActive })}>
        {children}
        
        {!isAdding ? (
          <div className="menu-item px-4 py-2">
            <button 
              className="btn btn-light-primary w-100 d-flex justify-content-start align-items-center p-2"
              onClick={handleAddClick}
              style={{ fontSize: '0.85rem' }}
            >
              <KTIcon iconName='plus' className='fs-4 me-2' />
              Add New Page
            </button>
          </div>
        ) : (
          <div className="menu-item px-3 py-2">
            <input
              type="text"
              className="form-control form-control-sm form-control-solid"
              placeholder="Page Name..."
              value={newPageName}
              onChange={(e) => setNewPageName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSaving}
              onBlur={() => {
                // To prevent conflict with Enter key, we only trigger create if it's not already saving
                if (!isSavingRef.current && newPageName.trim()) {
                  createPage(newPageName);
                } else if (!newPageName.trim()) {
                  setIsAdding(false);
                }
              }}
              autoFocus
            />
          </div>
        )}
        {[...pages].reverse().map(page => {
          const isHidden = hiddenPages.includes(page.id);
          const isPageActive = checkIsActive(pathname, `${to}/${page.id}`);
          return (
            <div key={page.id} className="menu-item">
              <div className="d-flex align-items-center position-relative pe-3 w-100">
                <Link 
                  className={clsx('menu-link without-sub flex-grow-1', { active: isPageActive })} 
                  to={`${to}/${page.id}`}
                  style={{ paddingRight: '60px' }}
                >
                  <span className='menu-bullet'>
                    <span className='bullet bullet-dot'></span>
                  </span>
                  <span className='menu-title' style={{ opacity: isHidden ? 0.5 : 1 }}>
                    {page.name}
                  </span>
                </Link>
                <div className="d-flex gap-1 position-absolute end-0 me-4 z-index-1">
                  <button 
                    className="btn btn-icon btn-sm text-muted btn-active-color-warning w-20px h-20px"
                    onClick={(e) => handleHideToggle(e, page.id, isHidden)}
                    title={isHidden ? "Show Page" : "Hide Page"}
                  >
                    <i className={clsx('bi', isHidden ? 'bi-eye-slash-fill text-warning' : 'bi-eye')}></i>
                  </button>
                  <button 
                    className="btn btn-icon btn-sm text-muted btn-active-color-danger w-20px h-20px"
                    onClick={(e) => handleDelete(e, page.id, page.name)}
                    title="Delete Page"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { SidebarMenuTemplateItem };
